/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { NoteCard, DESK_THEMES } from '../../types';
import {
  FileText,
  CheckSquare,
  Volume2,
  Image,
  Paintbrush,
  Heart,
  Lock,
  Tag,
  Clock,
  Trash2,
  Copy
} from 'lucide-react';

interface NoteCardItemProps {
  card: NoteCard;
  onClick: () => void;
  onToggleFavorite: (id: string, e: React.MouseEvent) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
  onDuplicate?: (card: NoteCard, e: React.MouseEvent) => void;
  onUnlockRequest?: (id: string, pin: string) => boolean; // returns true if matches
}

const stripHtml = (html: string): string => {
  if (!html) return '';
  let text = html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  text = text.replace(/<[^>]*>/g, ' ');
  text = text.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ');
  return text.trim();
};

export const NoteCardItem: React.FC<NoteCardItemProps> = ({
  card,
  onClick,
  onToggleFavorite,
  onDelete,
  onDuplicate,
}) => {
  const [showPinInput, setShowPinInput] = useState(false);
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState(false);

  // Helper formatting for UTC values
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getCardIcon = () => {
    switch (card.type) {
      case 'text': return <FileText className="w-4 h-4 text-slate-500" />;
      case 'checklist': return <CheckSquare className="w-4 h-4 text-emerald-600" />;
      case 'sketch': return <Paintbrush className="w-4 h-4 text-purple-600" />;
      case 'audio': return <Volume2 className="w-4 h-4 text-amber-600" />;
      case 'photo': return <Image className="w-4 h-4 text-sky-600" />;
    }
  };

  const totalTasks = card.checklistContent?.length || 0;
  const completedTasks = card.checklistContent?.filter(t => t.completed).length || 0;

  // Handle PIN validation
  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (enteredPin === card.pinCode) {
      setShowPinInput(false);
      setPinError(false);
      onClick(); // Open content
    } else {
      setPinError(true);
      setEnteredPin('');
    }
  };

  return (
    <div
      onClick={() => {
        if (card.isLocked) {
          setShowPinInput(true);
        } else {
          onClick();
        }
      }}
      className={`relative rounded-xl p-4 md:p-5 border flex flex-col justify-between shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 h-[210px] cursor-pointer group transition-all duration-300 ${card.deskThemeIdx !== undefined ? '' : card.colorClass}`}
      style={{
        backgroundColor: card.deskThemeIdx !== undefined ? DESK_THEMES[card.deskThemeIdx].bgValue : undefined,
        borderColor: card.deskThemeIdx !== undefined ? DESK_THEMES[card.deskThemeIdx].accentBorder : undefined,
        color: card.deskThemeIdx !== undefined ? DESK_THEMES[card.deskThemeIdx].textValue : undefined,
      }}
      id={`notecard-item-${card.id}`}
    >
      {/* Locked overlay cover */}
      {card.isLocked && !showPinInput && (
        <div className="absolute inset-0 bg-slate-900/10 backdrop-blur-md rounded-xl flex flex-col justify-center items-center z-10 p-4 text-center">
          <div className="w-12 h-12 rounded-full bg-slate-900/60 dark:bg-black/60 text-white flex items-center justify-center shadow-md animate-bounce duration-1000 mb-2">
            <Lock className="w-5 h-5" />
          </div>
          <span className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
            Card Secured
          </span>
          <p className="text-[10px] text-slate-600 dark:text-slate-400 mt-1">
            Click here to unlock pin
          </p>
        </div>
      )}

      {/* Unlock keypad dialog popup inline on the card */}
      {card.isLocked && showPinInput && (
        <div
          className="absolute inset-0 bg-white/95 dark:bg-slate-900/95 rounded-xl flex flex-col justify-center items-center z-20 p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setShowPinInput(false)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 text-xs font-bold"
          >
            Cancel
          </button>
          <Lock className="w-6 h-6 text-indigo-600 mb-1" />
          <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
            Enter 4-Digit PIN
          </span>
          <form onSubmit={handlePinSubmit} className="flex flex-col items-center mt-2 w-full max-w-[130px]">
            <input
              type="password"
              maxLength={4}
              value={enteredPin}
              onChange={(e) => {
                setEnteredPin(e.target.value);
                setPinError(false);
              }}
              placeholder="••••"
              className={`w-full text-center px-2 py-1.5 border rounded-lg text-lg font-bold font-mono tracking-wider focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                pinError ? 'border-rose-500 bg-rose-50' : 'border-slate-300 bg-slate-50 dark:bg-slate-950 dark:text-white'
              }`}
              autoFocus
            />
            {pinError && (
              <span className="text-[9px] text-rose-500 font-bold mt-1">
                Incorrect PIN code
              </span>
            )}
            <button
              type="submit"
              className="mt-2 text-[10px] bg-slate-800 text-white font-bold w-full py-1 rounded-sm uppercase tracking-wider"
            >
              Verify
            </button>
          </form>
        </div>
      )}

      {/* Header section (Icon & metadata action controls) */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-1.5 p-1 px-2 rounded-full bg-black/5 dark:bg-white/5 border border-black/5 text-slate-700 dark:text-slate-300">
          {getCardIcon()}
          <span className="text-[10px] font-bold uppercase tracking-wider">
            {card.type}
          </span>
        </div>

        {/* Favorite marker & Duplicate / deletion triggers */}
        <div className="flex items-center gap-1 pr-1 opacity-60 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => onToggleFavorite(card.id, e)}
            className={`p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors ${
              card.isFavorite ? 'text-rose-500' : 'text-slate-400 hover:text-slate-600'
            }`}
            title={card.isFavorite ? 'Remove from favorites' : 'Mark as favorite'}
          >
            <Heart className={`w-3.5 h-3.5 ${card.isFavorite ? 'fill-rose-500' : ''}`} />
          </button>
          
          {onDuplicate && (
            <button
              onClick={(e) => onDuplicate(card, e)}
              className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-slate-400 hover:text-slate-600 transition-colors"
              title="Duplicate note"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={(e) => onDelete(card.id, e)}
            className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-slate-400 hover:text-rose-600 transition-colors"
            title="Move to Recycle Bin"
            id={`notecard-delete-${card.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Title & Preview Content Section */}
      <div className="flex-1 mt-3 flex flex-col justify-start overflow-hidden">
        <h4 className="font-sans font-bold text-sm leading-snug text-slate-900 dark:text-slate-100 line-clamp-1 mb-1 shadow-xs">
          {card.title || 'Untitled Note'}
        </h4>

        {/* Display Card Body specifics */}
        <div className="flex-1 text-xs text-slate-600 dark:text-slate-300 font-sans mt-1 line-clamp-4 overflow-hidden">
          {card.type === 'text' && card.textContent && (
            <p className="whitespace-pre-line leading-relaxed leading-clamp-3">
              {stripHtml(card.textContent)}
            </p>
          )}

          {card.type === 'checklist' && card.checklistContent && (
            <div className="flex flex-col gap-1 w-full mt-1">
              {/* Completed tracking ring */}
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-full bg-black/10 dark:bg-white/10 rounded-full h-1">
                  <div
                    className="bg-emerald-500 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-[10px] font-mono text-slate-500 font-bold shrink-0">
                  {completedTasks}/{totalTasks}
                </span>
              </div>
              {card.checklistContent.slice(0, 3).map((item) => (
                <div key={item.id} className="flex items-center gap-1.5 text-[11px] leading-tight select-none">
                  <div className={`w-3 h-3 rounded-sm border shrink-0 flex items-center justify-center ${
                    item.completed ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-400'
                  }`}>
                    {item.completed && <span className="text-[8px] leading-none">✓</span>}
                  </div>
                  <span className={`truncate ${item.completed ? 'line-through opacity-50' : ''}`}>
                    {item.text}
                  </span>
                </div>
              ))}
              {totalTasks > 3 && (
                <span className="text-[10px] text-slate-400 italic block mt-0.5">
                  + {totalTasks - 3} more items...
                </span>
              )}
            </div>
          )}

          {card.type === 'sketch' && card.sketchDataUrl && (
            <div className="w-full h-20 rounded-md border border-black/5 bg-white dark:bg-slate-950 overflow-hidden flex items-center justify-center">
              <img
                src={card.sketchDataUrl}
                alt="Quick sketch draw"
                className="max-h-full object-contain p-1"
                referrerPolicy="no-referrer"
              />
            </div>
          )}

          {card.type === 'photo' && card.photoUrl && (
            <div className="w-full h-20 rounded-md border border-black/5 bg-white dark:bg-slate-950 overflow-hidden flex items-center justify-center">
              <img
                src={card.photoUrl}
                alt="Attached camera file"
                className="max-h-full w-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
          )}

          {card.type === 'audio' && (
            <div className="flex items-center gap-2 bg-black/5 dark:bg-white/5 border border-black/5 rounded-lg p-2 mt-1">
              <Volume2 className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-mono text-[10px] font-bold text-slate-600 dark:text-slate-400">
                  Voice Memo Card
                </span>
                <p className="text-[9px] text-slate-500 font-medium tracking-wide">
                  Duration: {card.audioDuration ? `${Math.floor(card.audioDuration / 60)}:${Math.floor(card.audioDuration % 60).toString().padStart(2, '0')}` : 'Voice'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer tags and indicators */}
      <div className="flex items-center justify-between border-t border-black/5 mt-3 pt-2 text-[10px] font-mono font-medium text-slate-500 whitespace-nowrap overflow-hidden">
        {/* Date updated indicator */}
        <span className="shrink-0">{formatDate(card.updatedAt)}</span>

        {/* Decorative elements: lock and tags */}
        <div className="flex items-center gap-1.5 ml-2 overflow-hidden select-none">
          {card.reminderTime && (
            <div className="flex items-center gap-0.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 p-0.5 px-1 rounded-xs" title="Reminder configured">
              <Clock className="w-2.5 h-2.5" />
            </div>
          )}
          {card.tags.length > 0 && (
            <div className="flex items-center gap-0.5 bg-black/5 dark:bg-white/5 p-0.5 px-1 rounded-xs">
              <Tag className="w-2.5 h-2.5 text-slate-400" />
              <span className="line-clamp-1 max-w-[50px]">{card.tags[0]}</span>
            </div>
          )}
        </div>
      </div>

      {/* BEAUTIFUL BENT SKEUOMORPHIC HIGHLIGHT CORNER CURVE */}
      <div 
        className="absolute bottom-0 right-0 w-8 h-8 pointer-events-none transition-all group-hover:w-10 group-hover:h-10 z-10"
        style={{
          background: `linear-gradient(135deg, transparent 45%, rgba(0,0,0,0.1) 50%, rgba(255,255,255,0.3) 55%, transparent 100%)`,
          boxShadow: `-4px -4px 6px -4px rgba(0,0,0,0.15)`
        }}
      />
      <div 
        className="absolute bottom-0 right-0 w-0 h-0 border-solid border-t-8 border-l-8 border-transparent transition-all group-hover:border-t-10 group-hover:border-l-10 z-10"
        style={{ borderBottomColor: 'transparent', borderRightColor: 'transparent' }}
      />
    </div>
  );
};
